import { createFileRoute, Link } from "@tanstack/react-router";
import { Badge } from "@timeghost/ui/shadcn/badge";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@timeghost/ui/shadcn/card";
import { Separator } from "@timeghost/ui/shadcn/separator";
import { Skeleton } from "@timeghost/ui/shadcn/skeleton";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminPeriodToggle from "@/components/admin/admin-period-toggle";
import BoxedPage from "@/components/shell/boxed-page";
import { useProfileFormat } from "@/lib/account/use-profile-format";
import {
	type AdminEntityPeriodCounts,
	type AdminStats,
	type AdminStatsPeriod,
	cachedAgo,
	periodValues,
	weekDelta,
	weekDeltaBadgeLabel,
} from "@/lib/admin/types";
import { useAdminStats } from "@/queries/admin/use-admin-stats.query";

const HUB_LINKS = [
	{ to: "/admin/users", titleKey: "admin.hub.users" },
	{ to: "/admin/workspaces", titleKey: "admin.hub.workspaces" },
	{ to: "/admin/subscriptions", titleKey: "admin.hub.subscriptions" },
	{ to: "/admin/notifications", titleKey: "admin.hub.notifications" },
	{ to: "/admin/stats", titleKey: "admin.hub.stats" },
] as const;

const WEEK_ENTITY_KEYS = [
	{ key: "users", labelKey: "admin.stats.users" },
	{ key: "workspaces", labelKey: "admin.stats.workspaces" },
	{ key: "clients", labelKey: "admin.stats.clients" },
	{ key: "projects", labelKey: "admin.stats.projects" },
	{ key: "tasks", labelKey: "admin.stats.tasks" },
] as const;

const WEEK_SUBSCRIPTION_KEYS = [
	{ key: "subscriptions", labelKey: "admin.stats.subscriptions" },
	{ key: "seats", labelKey: "admin.stats.seats" },
] as const;

const periodRange = (stats: AdminStats, period: AdminStatsPeriod): { readonly from: string; readonly to: string } => {
	if (period === "month") {
		return { from: stats.thisMonthStart, to: stats.nextMonthStart };
	}
	return { from: stats.thisWeekStart, to: stats.nextWeekStart };
};

const PeriodDeltaBadge = ({
	counts,
	locale,
	period,
}: {
	readonly counts: AdminEntityPeriodCounts;
	readonly locale: string;
	readonly period: AdminStatsPeriod;
}): JSX.Element => {
	const { t } = useTranslation();
	const { current, last } = periodValues(counts, period);
	const { signedCount } = weekDelta(current, last);
	const delta = weekDeltaBadgeLabel(current, last, locale);
	const variant = signedCount > 0 ? "default" : signedCount < 0 ? "destructive" : "secondary";
	const vsKey = period === "month" ? "admin.stats.vsLastMonth" : "admin.stats.vsLastWeek";
	return (
		<Badge aria-label={t(vsKey, { delta })} variant={variant}>
			{delta}
		</Badge>
	);
};

const PeriodStatCard = ({
	counts,
	label,
	locale,
	period,
}: {
	readonly counts: AdminEntityPeriodCounts;
	readonly label: string;
	readonly locale: string;
	readonly period: AdminStatsPeriod;
}): JSX.Element => {
	const { t } = useTranslation();
	const { current, last } = periodValues(counts, period);
	const lastKey = period === "month" ? "admin.stats.lastMonth" : "admin.stats.lastWeek";
	return (
		<Card size="sm">
			<CardHeader>
				<CardTitle>{label}</CardTitle>
				<CardDescription>{t(lastKey, { count: last.toLocaleString(locale) })}</CardDescription>
				<CardAction>
					<PeriodDeltaBadge counts={counts} locale={locale} period={period} />
				</CardAction>
			</CardHeader>
			<CardContent>
				<p className="font-medium text-2xl tabular-nums">{current.toLocaleString(locale)}</p>
			</CardContent>
		</Card>
	);
};

const StatCardGrid = ({
	items,
	locale,
	period,
	stats,
}: {
	readonly items: typeof WEEK_ENTITY_KEYS | typeof WEEK_SUBSCRIPTION_KEYS;
	readonly locale: string;
	readonly period: AdminStatsPeriod;
	readonly stats: AdminStats;
}): JSX.Element => {
	const { t } = useTranslation();
	return (
		<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
			{items.map((item) => (
				<PeriodStatCard
					counts={stats[item.key]}
					key={item.key}
					label={t(item.labelKey)}
					locale={locale}
					period={period}
				/>
			))}
		</div>
	);
};

const WeekStatsSkeleton = (): JSX.Element => (
	<div aria-busy className="flex flex-col gap-6">
		<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
			{WEEK_ENTITY_KEYS.map((item) => (
				<Card key={item.key} size="sm">
					<CardHeader>
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-3 w-32" />
					</CardHeader>
					<CardContent>
						<Skeleton className="h-8 w-16" />
					</CardContent>
				</Card>
			))}
		</div>
		<Separator />
		<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
			{WEEK_SUBSCRIPTION_KEYS.map((item) => (
				<Card key={item.key} size="sm">
					<CardHeader>
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-3 w-32" />
					</CardHeader>
					<CardContent>
						<Skeleton className="h-8 w-16" />
					</CardContent>
				</Card>
			))}
		</div>
	</div>
);

const AdminDashboardPage = (): JSX.Element => {
	const { t, i18n } = useTranslation();
	const profileFormat = useProfileFormat();
	const query = useAdminStats();
	const [period, setPeriod] = useState<AdminStatsPeriod>("week");
	const stats = query.data;
	const range = stats ? periodRange(stats, period) : null;

	return (
		<BoxedPage
			actions={<AdminPeriodToggle onChange={setPeriod} value={period} />}
			description={
				range && stats
					? `${t("admin.stats.weekRange", {
							from: profileFormat.date(new Date(range.from)),
							to: profileFormat.date(new Date(new Date(range.to).getTime() - 1)),
						})} · ${t("admin.stats.cachedAt", { date: cachedAgo(stats.computedAt, i18n.language) })}`
					: undefined
			}
			title={t("nav.adminDashboard")}>
			<div className="flex flex-col gap-6">
				{query.isPending ? <WeekStatsSkeleton /> : null}
				{query.isError ? <p className="text-destructive text-sm">{t("admin.loadFailed")}</p> : null}
				{stats ? (
					<div className="flex flex-col gap-6">
						<StatCardGrid items={WEEK_ENTITY_KEYS} locale={i18n.language} period={period} stats={stats} />
						<section className="flex flex-col gap-3">
							<Separator />
							<h2 className="font-medium text-sm">{t("admin.stats.subscriptions")}</h2>
							<StatCardGrid items={WEEK_SUBSCRIPTION_KEYS} locale={i18n.language} period={period} stats={stats} />
						</section>
					</div>
				) : null}
				<ul className="flex max-w-md flex-col gap-2">
					{HUB_LINKS.map((item) => (
						<li key={item.to}>
							<Link className="block rounded-lg border bg-muted px-3 py-2 text-sm hover:bg-accent" to={item.to}>
								{t(item.titleKey)}
							</Link>
						</li>
					))}
				</ul>
			</div>
		</BoxedPage>
	);
};

export const Route = createFileRoute("/_app/admin/")({
	component: AdminDashboardPage,
});
